import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { Metamodel, Owned } from 'shell-models-lib';
import { StringDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { Operator } from 'app/model/pipeline/copysource/sap-odp-selection';
import { SapSelectionOption, SapSelectionSign } from 'app/model/pipeline/copysource/sap-odp.copysource';

@Metamodel.model({})
export class SapOdpPartitionPredicate extends Owned {
    public static toDSL(model: SapOdpPartitionPredicate): string[] {
        const props: string[] = [];
        props.push(`'fieldName' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.fieldName)}`);
        props.push(`'sign' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.sign)}`);
        props.push(`'option' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.option)}`);
        props.push(`'low' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.low)}`);
        if (model.option.expression === SapSelectionOption.BT) {
            props.push(`'high' -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.high)}`);
        }
        return props;
    }

    public static toModel(owner: DataFlowSource, configs: Map<string, string>): SapOdpPartitionPredicate {
        const model = Owned.create<SapOdpPartitionPredicate>(SapOdpPartitionPredicate.MODEL, owner);
        model.fieldName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'fieldName'`), model);
        model.sign = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'sign'`), model);
        model.option = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'option'`), model);
        model.low = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'low'`), model);
        if (configs.get(`'high'`)) {
            model.high = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'high'`), model);
        }
        return model;
    }

    public get operator(): Operator {
        if (this.sign.expression === SapSelectionSign.Inclusive) {
            if (this.option.expression === SapSelectionOption.EQ) {
                return Operator.EQUAL;
            } else if (this.option.expression === SapSelectionOption.CP) {
                return Operator.LIKE;
            } else if (this.option.expression === SapSelectionOption.BT) {
                return Operator.BETWEEN;
            }
        } else if (this.sign.expression === SapSelectionSign.Exclusive) {
            if (this.option.expression === SapSelectionOption.EQ) {
                return Operator.NOT_EQUAL;
            } else if (this.option.expression === SapSelectionOption.CP) {
                return Operator.NOT_LIKE;
            } else if (this.option.expression === SapSelectionOption.BT) {
                return Operator.NOT_BETWEEN;
            }
        }
        return undefined;
    }

    public set operator(operator: Operator) {
        switch (operator) {
            case Operator.EQUAL:
                this.sign.expression = SapSelectionSign.Inclusive;
                this.option.expression = SapSelectionOption.EQ;
                break;
            case Operator.LIKE:
                this.option.expression = SapSelectionOption.CP;
                this.sign.expression = SapSelectionSign.Inclusive;
                break;
            case Operator.BETWEEN:
                this.option.expression = SapSelectionOption.BT;
                this.sign.expression = SapSelectionSign.Inclusive;
                break;
            case Operator.NOT_EQUAL:
                this.option.expression = SapSelectionOption.EQ;
                this.sign.expression = SapSelectionSign.Exclusive;
                break;
            case Operator.NOT_LIKE:
                this.option.expression = SapSelectionOption.CP;
                this.sign.expression = SapSelectionSign.Exclusive;
                break;
            case Operator.NOT_BETWEEN:
                this.option.expression = SapSelectionOption.BT;
                this.sign.expression = SapSelectionSign.Exclusive;
                break;
            default:
        }
    }

    public get showHigh(): boolean {
        return this.option.expression === SapSelectionOption.BT;
    }

    public fieldName: DataFlowExpression;
    public sign: DataFlowExpression;
    public option: DataFlowExpression;
    public low: DataFlowExpression;
    public high: DataFlowExpression;

    constructor() {
        super();
        this.fieldName = this._createTemplatableExpression();
        this.sign = this._createTemplatableExpression();
        this.option = this._createTemplatableExpression();
        this.low = this._createTemplatableExpression();
        this.high = this._createTemplatableExpression();
    }

    private _createTemplatableExpression(expectedType: string = StringDataFlowType.TYPE, defaultValue?: string): DataFlowExpression {
        const expression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);
        expression.requiredReturnType = expectedType;
        expression.expression = defaultValue;
        expression.isTemplatable = true;
        return expression;
    }
}
